import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Button, Icon, Modal } from 'Components';
import {
	Course,
	CourseDetailModalProps,
	Language
} from 'LearningManager/constants/createAssignment.constant';
import { LearningPath } from 'Services/Api/LearningManager/Types';
import { Course as CatalogCourse } from 'Services/Api/Courses/Types';
import { useStores } from 'Hooks/useStore';
import useLanguageNames from 'LearningManager/hooks/useLanguageNames';
import styles from './CourseDetailModal.module.scss';
import bannerImg from 'LearningManager/images/course-1.png';
import classNames from 'classnames';
import moment from 'moment';
import { pluralLabel } from 'LearningManager/helpers/role';
import { formatRunTime } from 'LearningManager/helpers/date';

// Placeholder for fields the activity API doesn't provide, so missing data is
// easy to spot.
const DASH = '-';

const formatCreatedDate = (value?: string | null) => {
	if (!value) return DASH;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('M/D/YYYY') : DASH;
};

// Legacy mock props are optional now that the modal is data-driven.
interface Props extends Partial<CourseDetailModalProps> {
	visible: boolean;
	onClose: () => void;
	// Single activity/course (Individual Courses cards).
	activityId?: number;
	// Learning path (multi-activity): renders the top activity as the header and
	// one accordion per activity in the path.
	learningPath?: LearningPath;
}

interface MetaInfoProps {
	title: string;
	value: string;
	icon: React.ReactNode;
}

interface AccordionProps {
	course: Course;
	expandedCourseId: string | null;
	toggleCourse: (id: string) => void;
	t: TFunction
}

// Adapts a catalog activity into the accordion's course shape, filling any gap
// (or a missing activity) with a dash. `subtitle` is the activity type name.
const toAccordionCourse = (
	course: CatalogCourse | undefined,
	order: number,
	subtitle: string,
	languages: string[]
): Course => ({
	id: String(course?.id ?? order),
	number: order,
	title: course?.name || DASH,
	subtitle: subtitle || DASH,
	description: course?.description || DASH,
	runTime: formatRunTime(course?.run_time),
	created: formatCreatedDate(course?.created_date),
	languages: languages.map((label) => ({ label }))
});

const MetaInfoCard: React.FC<MetaInfoProps> = ({ title, value, icon }) => {
	return (
		<div className={styles.card}>
			<div className={styles.label}>
				{icon}
				<span className={styles.head}>{title}</span>
			</div>
			<span className={styles.value}>{value}</span>
		</div>
	);
};

const LanguageTag = ({ lang }: { lang: Language }) => {
	return (
		<span key={lang.label} className={styles.badge}>
			{lang.label}
		</span>
	);
};

// Renders language tags, or a dash when none are available.
export const LanguageTags = ({ languages, label }: { languages: Language[], label: string }) => {
	return <div className={styles.languages}>
		<div className={styles.languages__label}>{label}</div>
		<div className={styles.languages__tags}>
			{languages.length ? languages.map((lang) => <LanguageTag key={lang.label} lang={lang} />) : (<span className={styles.badge}>{DASH}</span>)}
		</div>
	</div>
}

const Accordion = ({
	course,
	expandedCourseId,
	toggleCourse,
	t
}: AccordionProps) => {
	const isExpanded = expandedCourseId === course.id;

	return (
		<div key={course.id} className={styles.courseItem}>
			<div
				className={classNames(styles.accordion, {
					[styles['accordion--expanded']]: isExpanded
				})}
				onClick={() => toggleCourse(course.id)}
			>
				<span className={styles.number}>{course.number}</span>

				<div className={styles.label}>
					<div className={styles.accTitle}>{course.title}</div>
					<div className={styles.subtitle}>{course.subtitle}</div>
				</div>

				<Icon
					type="chevronUp"
					role="img"
					className={styles.chevron}
					style={{
						rotate: isExpanded ? '0deg' : '180deg'
					}}
				/>
			</div>

			<div
				className={classNames(styles.accordionContent, {
					[styles['accordionContent--expanded']]: isExpanded
				})}
			>
				<div className={styles.accordionBody}>
					<div>
						<div className={styles.bodyTitle}>{t('learning.courseDetailsModal.sections.description')}</div>
						<div className={styles.bodyDescription}>
							{course.description}
						</div>
					</div>

					<div className={styles.meta}>
						<MetaInfoCard
							title={t('learning.courseDetailsModal.fields.runTime')}
							icon={<Icon type="durationIcon" role="img" />}
							value={course.runTime}
						/>
						<MetaInfoCard
							title={t('learning.courseDetailsModal.fields.created')}
							icon={<Icon type="createdIcon" role="img" />}
							value={course.created}
						/>
					</div>

					<LanguageTags languages={course.languages} label={t('learning.courseDetailsModal.sections.availableLanguages')} />
				</div>
			</div>
		</div>
	);
};

const CourseDetailModal: React.FC<Props> = observer(
	({
		visible,
		onClose,
		activityId,
		learningPath,
		title,
		courseId,
		description,
		runTime,
		created,
		languages,
		courses
	}) => {
		const { learningManagerStore } = useStores();
		const getLanguageNames = useLanguageNames();
		const [expandedCourseId, setExpandedCourseId] = useState<string | null>(
			null
		);
		const [loading, setLoading] = useState(false);
		const { t } = useTranslation();

		const isLearningPathMode = Boolean(learningPath);
		const isActivityMode = Boolean(activityId) && !isLearningPathMode;

		// Single activity: fetch the one activity if not already cached.
		useEffect(() => {
			if (!visible || !activityId || learningPath) return;
			if (learningManagerStore.coursesById[activityId]) return;
			setLoading(true);
			learningManagerStore
				.fetchCourseById(activityId)
				.finally(() => setLoading(false));
		}, [visible, activityId, learningPath]);

		// Learning path: load the catalog so each activity in the path can be
		// looked up by id.
		useEffect(() => {
			if (!visible || !learningPath) return;
			setLoading(true);
			learningManagerStore
				.fetchAllCourses(100, learningManagerStore.allCoursesTenantId)
				.finally(() => setLoading(false));
		}, [visible, learningPath]);

		const detail = activityId
			? learningManagerStore.coursesById[activityId]
			: undefined;

		// Activities in the path, ordered, adapted to the accordion shape. The
		// subtitle shows each activity's type name.
		const activityTypesById = learningManagerStore.activityTypesById;
		const learningPathCourses = learningPath
			? [...learningPath.activities]
					.sort((a, b) => a.order - b.order)
					.map((activity, index) => {
						const course =
							learningManagerStore.coursesById[
								activity.activity_id
							];
						const typeName = course
							? activityTypesById[course.category_id]?.name ?? DASH
							: DASH;
						const languageIds = activity.language_ids?.length
							? activity.language_ids
							: course?.language_ids;
						return toAccordionCourse(
							course,
							activity.order || index + 1,
							typeName,
							getLanguageNames(languageIds)
						);
					})
			: [];
		// The top-most activity drives the header section.
		const topCourse = learningPathCourses[0];

		const toggleCourse = (id: string) => {
			setExpandedCourseId((prev) => (prev === id ? null : id));
		};

		const displayTitle = isLearningPathMode
			? learningPath?.name || DASH
			: isActivityMode
			? detail?.name ?? 'Course'
			: title;
		const displayId = isLearningPathMode
			? String(learningPath?.id)
			: isActivityMode
			? String(activityId)
			: courseId;
		const displayDescription = isLearningPathMode
			? topCourse?.description ?? DASH
			: isActivityMode
			? detail?.description ?? ''
			: description;

		// Meta / languages / accordions for the non-single-activity layout.
		const displayRunTime = isLearningPathMode ? DASH : runTime ?? DASH;
		const displayCreated = isLearningPathMode ? DASH : created ?? DASH;
		const learningPathLanguages = Array.from(
			new Set(
				learningPathCourses.flatMap((c) =>
					c.languages.map((lang) => lang.label)
				)
			)
		).map((label) => ({ label }));
		const activityLanguages = getLanguageNames(detail?.language_ids).map(
			(label) => ({ label })
		);
		const displayLanguages = isLearningPathMode
			? learningPathLanguages
			: languages ?? [];
		const displayCourses = isLearningPathMode
			? learningPathCourses
			: courses ?? [];

		return (
			<Modal
				visible={visible}
				onCancel={onClose}
				className={styles.modal}
				footer={
					<div className={styles.footer}>
						<Button type="primary" onClick={onClose}>
							{t('learning.courseDetailsModal.actions.close')}
						</Button>
					</div>
				}
				title={
					<div className={styles['title-container']}>
						<div className={styles.title}>{displayTitle}</div>
						<div className={styles['sub-title']}>
							{t('learning.courseDetailsModal.fields.id', {
								id: displayId
							})}
						</div>
					</div>
				}
				width={500}
			>
				{loading ? (
					<div className={styles.loading}>
						<Spin />
					</div>
				) : (
					<>
						<div className={styles.banner}>
							<img
								src={
									(isActivityMode && detail?.image_url) ||
									bannerImg
								}
								className={styles.img}
								alt={t('learning.courseDetailsModal.bannerAlt')}
							/>
						</div>
			
						<div className={styles.description}>
							<div className={styles.heading}>
								{t('learning.courseDetailsModal.sections.description')}
							</div>
							<div className={styles.desc}>
								{displayDescription}
							</div>
						</div>
			
						{isActivityMode ? (
							<>
								<div className={styles.meta}>
									<MetaInfoCard
										title={t(
											'learning.courseDetailsModal.fields.runTime'
										)}
										icon={
											<Icon type="durationIcon" role="img" />
										}
										value={formatRunTime(detail?.run_time)}
									/>
									<MetaInfoCard
										title={t(
											'learning.courseDetailsModal.fields.created'
										)}
										icon={
											<Icon type="createdIcon" role="img" />
										}
										value={formatCreatedDate(detail?.created_date)}
									/>
								</div>
			
								<LanguageTags languages={activityLanguages} label={t('learning.courseDetailsModal.sections.availableLanguages')} />
							</>
						) : (
							<>
								<div className={styles.meta}>
									<MetaInfoCard
										title={t(
											'learning.courseDetailsModal.fields.runTime'
										)}
										icon={
											<Icon type="durationIcon" role="img" />
										}
										value={displayRunTime}
									/>
									<MetaInfoCard
										title={t(
											'learning.courseDetailsModal.fields.created'
										)}
										icon={
											<Icon type="createdIcon" role="img" />
										}
										value={displayCreated}
									/>
								</div>
			
								<LanguageTags languages={displayLanguages} label={t('learning.courseDetailsModal.sections.availableLanguages')} />
			
								<div className={styles.courses}>
									<div className={styles.title}>
										{t(
											pluralLabel('learning.courseDetailsModal.sections.courses', displayCourses.length),
											{ count: displayCourses.length }
										)}
									</div>
			
									<div className={styles.courseList}>
										{displayCourses.map((course) => (
											<Accordion
												key={course.id}
												t={t}
												course={course}
												expandedCourseId={expandedCourseId}
												toggleCourse={toggleCourse}
											/>
										))}
									</div>
								</div>
							</>
						)}
					</>
				)}
			</Modal>
		);
	}
);

export default CourseDetailModal;
